// Melbourne Cup sweep — the arithmetic, shared by the admin page and cup-sweep-sb.
//
// A PERSON (the payer) enters with their name and mobile and gets ONE payment reference
// (CUP-K7Q3M). Under it they buy tickets: each ticket is one name in one sweep, their own
// or someone they nominate. Paul records the money that arrives against the person, and the
// money pays for their tickets in the order they bought them. Only a paid ticket is drawn a
// horse, at random, from the runners nobody else in that sweep holds. A scratched horse
// gives its ticket back to the hat: the ticket is redrawn from what's left.
//
// Sweeps: $2 / $5 / $10 for the team and the public, plus a Family sweep and an Extended
// family sweep that only show to people holding their private link.
//
// CommonJS so the Netlify function can require it (like atoPlan.js). Keep it free of spread
// and for…of, or the web build breaks its exports.

const DEFAULT_SPLIT = [60, 30, 10];
// Prizes are FIXED dollar amounts Paul sets per sweep and guarantees (Paul, 2 Nov 2026), so a
// punter sees exactly what 1st/2nd/3rd win before buying. They start at what a sold-out field
// pays: 24 tickets x price, split 60/30/10, in whole dollars (1st takes the rounding).
const FULL_FIELD = 24;
function defaultPrizes(price) {
  const pot = FULL_FIELD * price;
  const second = Math.round(pot * DEFAULT_SPLIT[1] / 100);
  const third = Math.round(pot * DEFAULT_SPLIT[2] / 100);
  return [pot - second - third, second, third];
}
const prizesOk = (p) => Array.isArray(p) && p.length === 3 && p.every((n) => Number(n) >= 0 && Number(n) <= 100000);
// Flemington, first Tuesday in November, 3:00pm AEDT.
const RACE_AT = '2026-11-03T15:00:00+11:00';

// kind: 'team' sweeps are on the public page; 'family' / 'extended' only with their link key.
const DEFAULT_SWEEPS = [
  { id: 's2', kind: 'team', label: '$2 sweep', price: 2, enabled: true },
  { id: 's5', kind: 'team', label: '$5 sweep', price: 5, enabled: true },
  { id: 's10', kind: 'team', label: '$10 sweep', price: 10, enabled: true },
  { id: 'fam', kind: 'family', label: 'Family sweep', price: 50, enabled: true },
  { id: 'ext', kind: 'extended', label: 'Extended family sweep', price: 20, enabled: false },
];

const isPrivate = (s) => s.kind !== 'team';

// Stored sweep settings laid over the defaults, so a new default appears without a migration.
// A sweep can also run extra POOLS (Paul, 2 Nov 2026): when the $2 sweep's 24 horses are gone,
// "$2 sweep #2" opens with its own 24 horses, prizes and draw. A pool is stored as its own sweep
// {id: 's2-2', poolOf: 's2', pool: 2}; it copies its parent's kind (and private link). Once a sweep
// has pools, the original is #1 and every pool carries `poolNo` so labels say which one.
function mergeSweeps(stored) {
  const byId = {};
  (stored || []).forEach((s) => { if (s && s.id) byId[s.id] = s; });
  const one = (d, s, extra) => {
    const price = Number(s.price) > 0 ? Number(s.price) : d.price;
    return Object.assign({
      id: d.id, kind: d.kind,
      label: d.kind === 'team' ? '$' + price + ' sweep' : d.label,
      price: price,
      enabled: typeof s.enabled === 'boolean' ? s.enabled : d.enabled,
      open: s.open !== false,
      split: splitOk(s.split) ? s.split.map(Number) : DEFAULT_SPLIT.slice(),
      prizes: prizesOk(s.prizes) ? s.prizes.map(Number) : defaultPrizes(price),
      key: s.key || null,
      poolOf: null, pool: 1, poolNo: null,
    }, extra || {});
  };
  const out = [];
  DEFAULT_SWEEPS.forEach((d) => {
    const base = one(d, byId[d.id] || {});
    const pools = (stored || []).filter((s) => s && s.poolOf === d.id && Number(s.pool) >= 2)
      .sort((x, y) => Number(x.pool) - Number(y.pool))
      .map((s) => one(d, s, { id: s.id, poolOf: d.id, pool: Number(s.pool) }));
    out.push(base);
    if (pools.length) {
      base.poolNo = 1; base.label += ' #1';
      pools.forEach((p) => { p.poolNo = p.pool; p.label += ' #' + p.pool; out.push(p); });
    }
  });
  return out;
}

// The next pool for a sweep: {id, poolOf, pool, price, prizes, enabled, open} to store.
function nextPool(sweeps, baseId) {
  const base = (sweeps || []).filter((s) => s.id === baseId && !s.poolOf)[0];
  if (!base) return null;
  const n = (sweeps || []).filter((s) => s.poolOf === baseId).reduce((m, s) => Math.max(m, s.pool), 1) + 1;
  return { id: baseId + '-' + n, poolOf: baseId, pool: n, price: base.price, prizes: base.prizes.slice(), split: base.split.slice(), enabled: true, open: true };
}

// A pasted field, one runner per line. Accepts what the racing sites print:
//   "1 Horse Name (5)"   "1. Horse Name  barrier 5"   "1\tHorse Name\t5"   "Horse Name"
// The saddlecloth number leads; a barrier is the number in brackets or the last number.
function parseField(text) {
  const out = [];
  String(text || '').split(/\r?\n/).forEach((raw) => {
    let line = raw.replace(/\s+/g, ' ').trim();
    if (!line) return;
    let no = null; let barrier = null;
    const lead = line.match(/^(\d{1,2})[.)\-:\s]+(.*)$/);
    if (lead) { no = parseInt(lead[1], 10); line = lead[2].trim(); }
    const br = line.match(/\((\d{1,2})\)/) || line.match(/\bbarrier\s*(\d{1,2})\b/i) || line.match(/\s(\d{1,2})$/);
    if (br) { barrier = parseInt(br[1], 10); line = line.replace(br[0], ' ').trim(); }
    const name = line.replace(/\s{2,}/g, ' ').replace(/[\s\-–,]+$/, '').trim();
    if (!name) return;
    out.push({ no: no || out.length + 1, name: name, barrier: barrier, scratched: false });
  });
  return out;
}

const runners = (field) => (field || []).filter((h) => !h.scratched);

// Betting odds (decimal, e.g. 6.5 = $6.50), so a punter can tell a real chance from a donkey.
// They're a snapshot Paul loads (source + time shown with them) — the market moves to the jump.
// Pasted lines like "Point Of Law $6.50", "20 Point Of Law 6.5" or "20 6.50": the odds are the
// last number; the horse is the longest field name found in the line, else a leading number.
const normName = (x) => String(x || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
function parseOdds(text, field) {
  const out = {}; const unmatched = [];
  String(text || '').split(/\r?\n/).forEach((raw) => {
    const line = raw.replace(/\s+/g, ' ').trim();
    if (!line) return;
    const m = line.match(/\$?\s*(\d+(?:\.\d+)?)\s*$/);
    if (!m) { unmatched.push(line); return; }
    const odds = Number(m[1]);
    const rest = line.slice(0, m.index).trim();
    const n = ' ' + normName(rest) + ' ';
    let horse = null;
    (field || []).forEach((h) => {
      const hn = normName(h.name);
      if (hn && n.indexOf(' ' + hn + ' ') >= 0 && (!horse || hn.length > normName(horse.name).length)) horse = h;
    });
    if (!horse) {
      const lead = rest.match(/^#?(\d{1,2})\b/);
      if (lead) horse = (field || []).filter((h) => h.no === Number(lead[1]))[0] || null;
    }
    if (horse && odds >= 1.01 && odds <= 1001) out[horse.no] = odds; else unmatched.push(line);
  });
  return { odds: out, unmatched: unmatched };
}

// A plain-English tag from the odds: the shortest-priced runner is THE favourite.
function oddsTier(horse, field) {
  if (!horse || !(horse.odds > 0) || horse.scratched) return null;
  const best = Math.min.apply(null, runners(field).filter((h) => h.odds > 0).map((h) => h.odds));
  if (horse.odds === best) return 'fav';
  if (horse.odds <= 12) return 'top';
  if (horse.odds <= 26) return 'mix';
  if (horse.odds <= 50) return 'out';
  return 'roughie';
}
const live = (list) => (list || []).filter((e) => !e.deletedAt);
const cents = (n) => Math.round(Number(n || 0) * 100);

// Prize money from a pot: 1st takes any rounding remainder, so the three always add up.
function prizes(pot, split) {
  const s = splitOk(split) ? split : DEFAULT_SPLIT;
  const c = cents(pot);
  const second = Math.floor(c * (Number(s[1]) || 0) / 100);
  const third = Math.floor(c * (Number(s[2]) || 0) / 100);
  return [(c - second - third) / 100, second / 100, third / 100];
}

function splitOk(split) {
  return Array.isArray(split) && split.length === 3
    && split.every((n) => Number(n) >= 0) && Math.round(split.reduce((a, n) => a + Number(n), 0)) === 100;
}

// What a person owes and what their money covers. Tickets are paid in the order they were
// bought; a ticket the remaining money can't cover is skipped, so a later cheaper one can
// still go in. Removed tickets and voided payments don't count.
function account(payer, tickets) {
  const mine = live(tickets).filter((t) => t.payerId === payer.id)
    .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
  const received = live(payer.payments).reduce((a, p) => a + cents(p.amount), 0);
  let left = received;
  const paidIds = {};
  mine.forEach((t) => {
    const c = cents(t.price);
    if (left >= c) { paidIds[t.id] = true; left -= c; }
  });
  const due = mine.reduce((a, t) => a + cents(t.price), 0);
  return {
    tickets: mine.length, due: due / 100, received: received / 100,
    balance: Math.max(0, due - received) / 100, credit: left / 100,
    paidIds: paidIds, paidCount: Object.keys(paidIds).length,
  };
}

// Where a sweep stands: paid tickets, the pot, the prizes, and the horses still in the hat.
function sweepSummary(sweep, tickets, field) {
  const mine = live(tickets).filter((t) => t.sweep === sweep.id);
  const paid = mine.filter((t) => t.paid);
  const taken = {};
  paid.forEach((t) => { if (t.horse != null) taken[t.horse] = true; });
  const pool = runners(field).filter((h) => !taken[h.no]);
  const pot = paid.reduce((a, t) => a + cents(t.price), 0) / 100;
  return {
    id: sweep.id, price: sweep.price, label: sweep.label,
    entries: mine.length, paid: paid.length,
    waiting: paid.filter((t) => t.horse == null).length,
    runners: runners(field).length,
    spotsLeft: field && field.length ? Math.max(0, runners(field).length - paid.length) : null,
    // pot = money collected; prizes = what Paul has set and guarantees; the gap is his to cover or keep.
    pot: pot, prizes: (sweep.prizes || defaultPrizes(sweep.price)).slice(),
    prizeTotal: (sweep.prizes || defaultPrizes(sweep.price)).reduce((a, n) => a + Number(n), 0),
    pool: pool.map((h) => h.no),
  };
}

// Draw a horse for every paid ticket in the sweep that hasn't got one, oldest payment first.
// `rand(n)` returns an integer in [0, n) — crypto.randomInt on the server. Returns the
// tickets it drew for ({id, horse}); tickets left over when the hat is empty stay undrawn.
function draw(sweepId, tickets, field, rand) {
  const mine = live(tickets).filter((t) => t.sweep === sweepId && t.paid);
  const taken = {};
  mine.forEach((t) => { if (t.horse != null) taken[t.horse] = true; });
  const hat = runners(field).filter((h) => !taken[h.no]).map((h) => h.no);
  const waiting = mine.filter((t) => t.horse == null)
    .sort((a, b) => String(a.paidAt || a.createdAt).localeCompare(String(b.paidAt || b.createdAt)));
  const drawn = [];
  waiting.forEach((t) => {
    if (!hat.length) return;
    const i = rand(hat.length);
    drawn.push({ id: t.id, horse: hat.splice(i, 1)[0] });
  });
  return drawn;
}

// Results → winners in a sweep. A placing horse nobody holds is reported as unsold; what
// happens to that prize is Paul's call, not the engine's.
function winners(sweep, tickets, field, results) {
  const r = results || {};
  const summary = sweepSummary(sweep, tickets, field);
  const paid = live(tickets).filter((t) => t.sweep === sweep.id && t.paid);
  return [r.first, r.second, r.third].map((no, i) => {
    if (no == null || no === '') return null;
    const n = Number(no);
    const horse = (field || []).filter((h) => h.no === n)[0] || null;
    const holder = paid.filter((t) => t.horse === n)[0] || null;
    return { place: i + 1, horse: n, horseName: horse ? horse.name : '', ticket: holder, prize: summary.prizes[i] };
  });
}

// What the public board shows of a name: first name + surname initial.
function shortName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.';
}

// The payment reference people put in their PayID description.
const payRef = (payer) => 'CUP-' + payer.ref;

module.exports = {
  DEFAULT_SPLIT, FULL_FIELD, defaultPrizes, prizesOk, RACE_AT, parseOdds, oddsTier, nextPool, DEFAULT_SWEEPS, isPrivate, mergeSweeps, parseField, runners, live,
  prizes, splitOk, account, sweepSummary, draw, winners, shortName, payRef,
};
